// ── UPLOAD RETRY INDICATOR ──
// A soft, button-free pill that shows the storyteller (or captain in proxy
// mode) that we're still in the middle of saving their last interview.
// Hidden when there's nothing pending. The retry hook itself runs on every
// authenticated page load and re-attempts uploads in the background — this
// component just gives a calm visual confirmation that things are still in
// progress so the user doesn't think the app is broken.
//
// Click the pill to open a details panel that lists every pending item and
// lets the user either Force retry it or Discard it. Discarding is the
// escape hatch for genuinely stuck uploads (e.g., the session never
// started so /sessions/start keeps 4xxing). Discard deletes the local
// audio blob too — there's a warning before it commits.
//
// Mounted globally in App.jsx so it follows the user across pages.

import { useEffect, useState } from 'react';
import { useUploadRetry } from '@/hooks/useUploadRetry.js';
import { useAuth } from '@/context/AuthContext.jsx';
import { listPending, removePending } from '@/utils/uploadQueue.js';
import { processQueueItem } from '@/services/uploadRecovery.js';

function fmtRelative(iso) {
  if (!iso) return '—';
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return secs + 's ago';
  if (secs < 3600) return Math.floor(secs / 60) + 'm ago';
  if (secs < 86400) return Math.floor(secs / 3600) + 'h ago';
  return Math.floor(secs / 86400) + 'd ago';
}

function fmtBytes(n) {
  if (!n) return '—';
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return Math.round(n / 1024) + ' KB';
  return (n / (1024 * 1024)).toFixed(1) + ' MB';
}

export function UploadRetryIndicator() {
  const auth = useAuth();
  // Only run the hook when there's actually a logged-in user. Avoids
  // poking IndexedDB on the auth/login pages.
  const enabled = !!auth?.profile?.id;
  const { pendingCount, refreshCount } = useUploadRetry({ enabled });

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null); // id of the item currently being acted on
  const [confirm, setConfirm] = useState(null); // item pending discard confirm

  async function loadItems() {
    try { setItems(await listPending()); } catch {}
  }
  useEffect(() => { if (open) loadItems(); }, [open]);

  async function forceRetry(item) {
    setBusy(item.id);
    try { await processQueueItem(item); } catch {}
    await loadItems();
    await refreshCount();
    setBusy(null);
  }

  async function discard(item) {
    setBusy(item.id);
    try { await removePending(item.id); } catch {}
    await loadItems();
    await refreshCount();
    setBusy(null);
    setConfirm(null);
  }

  if (!enabled || pendingCount === 0) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Show pending uploads"
        style={{
          position: 'fixed',
          bottom: '16px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#FDF9F4',
          border: '1.5px solid #C9A84C',
          borderRadius: '100px',
          padding: '10px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontSize: '13px',
          fontFamily: 'var(--font-sans)',
          color: '#5C4433',
          boxShadow: '0 4px 18px rgba(46, 32, 25, 0.12)',
          zIndex: 600,
          maxWidth: '92vw',
          textAlign: 'center',
          cursor: 'pointer',
        }}
      >
        <span
          style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            background: '#C9A84C',
            flexShrink: 0,
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
        <span>
          Finishing up your last interview…
        </span>
        <span style={{ fontSize: '11px', opacity: 0.7, marginLeft: '4px' }}>tap for details</span>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 0.4; transform: scale(0.85); }
            50%      { opacity: 1;    transform: scale(1.1); }
          }
        `}</style>
      </button>

      {/* Details panel */}
      {open && (
        <div onClick={() => setOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(46,32,25,0.6)', zIndex: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--cream)', borderRadius: '20px', padding: '24px',
            maxWidth: '480px', width: '100%', maxHeight: '85vh', overflowY: 'auto',
            fontFamily: 'var(--font-sans)', boxShadow: '0 16px 60px rgba(46,32,25,0.4)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
              <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '20px', fontWeight: 400 }}>
                Pending uploads
              </h3>
              <button onClick={() => setOpen(false)} style={{
                background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px',
                color: 'var(--text-hint)', lineHeight: 1, padding: '4px 8px',
              }}>×</button>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '14px' }}>
              These items are waiting to finish uploading. The app retries automatically every 30 seconds.
              If something looks stuck (lots of attempts and no progress), you can <strong>Force retry</strong> right now
              or <strong>Discard</strong> to remove it.
              <br />
              <span style={{ color: '#8B3A1C' }}>
                Discard deletes the local audio for that interview — only use it if you're sure you don't need it.
              </span>
            </p>

            {items.length === 0 ? (
              <p style={{ fontSize: '13px', color: 'var(--text-hint)', fontStyle: 'italic', padding: '12px 0' }}>
                Nothing pending. (Indicator will hide once the queue check refreshes.)
              </p>
            ) : items.map(it => {
              const bytes = it.audioBlob && it.audioBlob.size;
              const stepDone = [
                it.sessionId ? 'session' : null,
                it.audioUploaded ? 'audio' : null,
                it.completed ? 'complete' : null,
                it.audioVerified ? 'verified' : null,
              ].filter(Boolean).join(' / ') || 'no steps yet';
              return (
                <div key={it.id} style={{
                  background: 'var(--warm-white)', border: '1px solid var(--tan)',
                  borderRadius: '12px', padding: '12px 14px', marginBottom: '10px',
                }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>
                    {it.mode === 'freestyle' ? 'Freestyle recording' : 'Interview'}
                    {it.kind && it.kind !== 'session' && (
                      <span style={{ marginLeft: '6px', fontSize: '10px', background: 'var(--tan)', color: 'var(--text-secondary)', padding: '1px 6px', borderRadius: '100px' }}>{it.kind}</span>
                    )}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-hint)', lineHeight: 1.6 }}>
                    <div>Audio size: {fmtBytes(bytes)}</div>
                    <div>Session id: <span style={{ fontFamily: 'monospace' }}>{it.sessionId || '(not started)'}</span></div>
                    <div>Steps done: {stepDone}</div>
                    <div>Attempts: {it.attempts || 0}{it.lastTried ? ' · last ' + fmtRelative(it.lastTried) : ''}</div>
                    <div>Started: {fmtRelative(it.createdAt)}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                    <button
                      onClick={() => forceRetry(it)}
                      disabled={busy === it.id}
                      style={{
                        flex: 1, background: 'var(--brown-dark)', color: 'var(--cream)', border: 'none',
                        borderRadius: '100px', padding: '8px 14px', fontSize: '12px', fontWeight: 500,
                        cursor: 'pointer', opacity: busy === it.id ? 0.6 : 1,
                      }}
                    >
                      {busy === it.id ? 'Working…' : 'Force retry'}
                    </button>
                    <button
                      onClick={() => setConfirm(it)}
                      disabled={busy === it.id}
                      style={{
                        flex: 1, background: 'var(--warm-white)', color: '#8B3A1C',
                        border: '1.5px solid #D07A5C', borderRadius: '100px',
                        padding: '8px 14px', fontSize: '12px', fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      Discard
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Discard confirmation */}
      {confirm && (
        <div onClick={() => busy ? null : setConfirm(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(46,32,25,0.7)', zIndex: 800,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--cream)', borderRadius: '20px', padding: '24px',
            maxWidth: '380px', width: '100%', textAlign: 'center',
            fontFamily: 'var(--font-sans)',
          }}>
            <div style={{ fontSize: '38px', marginBottom: '10px' }}>⚠</div>
            <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '20px', fontWeight: 400, marginBottom: '8px' }}>
              <em>Discard this upload?</em>
            </h3>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '18px' }}>
              The local audio for this recording will be deleted from your device.
              {confirm.audioVerified
                ? ' The server already confirmed it has the audio, so the interview is safe.'
                : ' The server has NOT yet confirmed it received the audio — if you discard now, the audio for this interview may be lost.'}
            </p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setConfirm(null)}
                disabled={busy === confirm.id}
                style={{ flex: 1, background: 'var(--warm-white)', border: '1.5px solid var(--tan)', borderRadius: '100px', padding: '12px', fontSize: '14px', cursor: 'pointer' }}
              >Cancel</button>
              <button
                onClick={() => discard(confirm)}
                disabled={busy === confirm.id}
                style={{ flex: 1, background: 'var(--red-soft)', color: '#fff', border: 'none', borderRadius: '100px', padding: '12px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
              >{busy === confirm.id ? 'Discarding…' : 'Yes, discard'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
